import React, { useCallback, useEffect, useRef } from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { getReadableTextColor, hasReadableContrast, shiftToneForSubtleContrast } from '@shared/utils/color';
import { useSmartAppBannerProvider } from '@shared/components/SmartAppBanner';
import { useWebsiteBannerOffset } from '@shared/components/WebsiteBanner';
import { useColorPaletteProvider } from '../../layouts/ColorPaletteProvider/ColorPaletteProvider';
import { useWeddingCodeBannerOffset } from './WeddingCodeBannerOffsetProvider';

export type WeddingCodeBannerProps = Readonly<{
  /** The full banner copy, with the wedding code already interpolated. */
  message: string;
  /** Concise accessible name announced by screen readers instead of the full marketing copy. */
  ariaLabel: string;
  /** Invoked when the guest activates the banner — navigates to the accommodations page. */
  onNavigate: () => void;
}>;

/**
 * Guest-facing wedding-code banner — a full-width strip pinned at the very top of the wedding
 * website (above the hero), colored from the couple's own theme and linking to accommodations.
 *
 * It is `position: fixed` and sits below any smart-app / website banner (`top` = their offset). To
 * avoid the hero overlapping it, the banner measures its own height and reports it via
 * `useWeddingCodeBannerOffset`; `GuestSiteBody` adds that to the page's top padding so content
 * starts below the bar — the same reserve-space pattern the smart-app banner uses.
 *
 * Colors come from `useColorPaletteProvider()`: the bar is derived from `artBackgroundColor` and the
 * text uses the couple's `artBackgroundFontColor` (matching the header, per the Figma). Rather than
 * using the theme color verbatim — which makes the bar blend into the page — we nudge it a few tones
 * (lighter for dark palettes, darker for light ones) so the bar stays cohesive but stands out subtly.
 * If the theme font color wouldn't be legible on the shifted bar — or is missing — it falls back to a
 * derived readable color.
 */
export const WeddingCodeBanner: React.FC<WeddingCodeBannerProps> = ({ message, ariaLabel, onNavigate }) => {
  const { artBackgroundColor, artBackgroundFontColor } = useColorPaletteProvider();
  const { smartAppBannerHeight } = useSmartAppBannerProvider();
  const { bannerOffset } = useWebsiteBannerOffset();
  const { setWeddingBannerHeight } = useWeddingCodeBannerOffset();

  // Shift a few tones off the theme background so the bar separates subtly from the page instead of
  // blending in (per design). Legibility is re-checked against this shifted color, not the original.
  // The hardcoded fallback is used verbatim — with no theme color there's nothing to offset from.
  const backgroundColor = artBackgroundColor ? shiftToneForSubtleContrast(artBackgroundColor) : '#1d1d1f';
  const themeFontColorIsLegible = Boolean(artBackgroundFontColor) && hasReadableContrast(backgroundColor, artBackgroundFontColor as string);
  const textColor = themeFontColorIsLegible ? (artBackgroundFontColor as string) : getReadableTextColor(backgroundColor);

  // Sit below any smart-app / website banner already pinned at the top.
  const topOffset = Math.max(smartAppBannerHeight, bannerOffset);

  // Report the rendered height so the page reserves space for the fixed bar (and stops reserving
  // it on unmount). Re-measure on viewport resize, which is when the copy can wrap to more lines.
  const bannerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = bannerRef.current;
    if (!element) {
      return;
    }
    const updateHeight = () => {
      setWeddingBannerHeight(element.offsetHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => {
      window.removeEventListener('resize', updateHeight);
      setWeddingBannerHeight(0);
    };
  }, [setWeddingBannerHeight, message]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onNavigate();
      }
    },
    [onNavigate],
  );

  return (
    <Box
      ref={bannerRef}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onNavigate}
      onKeyDown={handleKeyDown}
      position="fixed"
      top={topOffset}
      left={0}
      right={0}
      width="100%"
      zIndex="banner"
      display="flex"
      alignItems="center"
      justifyContent="center"
      paddingX={6}
      paddingY={pxToRem(12)}
      style={{ backgroundColor, cursor: 'pointer' }}
    >
      <TextV2
        typographyVariant={{ _: 'label2', md: 'label3' }}
        textAlign="center"
        style={{ color: textColor }}
      >
        {message}
      </TextV2>
    </Box>
  );
};

WeddingCodeBanner.displayName = 'WeddingCodeBanner';
